import { Role, SchoolUserRole, Voucher } from '@novaheal/types';
import { useQuery, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { axios } from 'utils/axios';

export type DashboardData = {
  allSchools: School[];
  schoolRolesWithVoucher: SchoolRolesWithVoucher;
};

export type School = {
  id: string;
  name: string;
};

export type SchoolRolesWithVoucher = {
  [key: string]: {
    b2bSchoolId: string;
    b2bSchoolName: string;
    createdAt: string;
    roles: Role;
    updatedAt: string;
    userId: string;
    vouchers: SchoolVoucher[];
    allVouchers: SchoolVoucher[];
    timesUsed: number;
  };
};

export type SchoolVoucher = {
  shortCode: string;
  id: string;
  status: string;
  validUntil: Date;
  type: string;
  timesUsed: number;
  allowedUsage: number;
};

export type SchoolUsers = {
  data: SchoolUser[];
  totalPages: number;
  totalUsers: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
};

export type AssignedClass = {
  id: string;
  name: string;
};

export type SchoolUser = {
  b2bSchoolId: string;
  educationStatus: string;
  email: string;
  firstName: string;
  id: string;
  subscriptionStatus: string; // not needed
  subscriptionUpdatedAt: string; // not needed
  voucherId: string;
  voucherShortCode: string;
  lastName?: string;
  schoolRoles?: { role: string }[]; // not needed
  isTeacher: boolean;
  isGuide: boolean;
  memberOfClasses: AssignedClass[];
  teacherOfClasses: AssignedClass[];
  guideOfClasses: AssignedClass[];
};

export enum AdminInviteResponse {
  INVITE_SUCCESS = 'inviteSuccess',
  INVITE_ERROR = 'inviteError',
}

const getUsers = async (
  voucherId?: string,
  page?: number,
  searchQuery?: string,
  sortBy?: string,
  sortOrder?: string,
  excludeClassId?: string,
  role?: SchoolUserRole[],
  all?: boolean,
  limit?: number
): Promise<SchoolUsers | null> => {
  const { data: userManagementContent } = await axios.get<SchoolUsers>('/management/user', {
    params: {
      voucherId,
      page,
      searchQuery,
      sortBy,
      sortOrder,
      ...(excludeClassId && { excludeClassId }),
      ...(role && role.length > 0 && { role: role.join(',') }),
      ...(all && { all: true }),
      ...(limit && { limit }),
    },
  });

  return userManagementContent;
};

const kickUserFromOrganization = async (userId: string, voucherId: string): Promise<void> => {
  const { data: response } = await axios.post<void>('/management/user', {
    userId,
    voucherId,
  });
  return response;
};

const inviteUsers = async (params: {
  emails: string[];
  voucherId: string;
  role?: SchoolUserRole.TEACHER | SchoolUserRole.GUIDE;
}): Promise<AdminInviteResponse> => {
  const { emails, voucherId, role } = params;

  try {
    await axios.post<void>('/management/user/invite', {
      voucherId,
      emails,
      ...(role && { role }),
    });

    return AdminInviteResponse.INVITE_SUCCESS;
  } catch (error) {
    const status = (error as AxiosError).response?.status;

    if (status === 400) {
      return AdminInviteResponse.INVITE_ERROR;
    }
    throw error;
  }
};

const changeUserRole = async (params: {
  userId: string;
  role: SchoolUserRole;
  confirmChange?: boolean;
}): Promise<void> => {
  const { userId, role, confirmChange } = params;
  const queryParams = confirmChange ? `?confirmChange=${confirmChange}` : '';
  await axios.patch<void>(`/management/user/role${queryParams}`, {
    userId,
    role,
  });
};

type VouchersResponse = {
  data: Voucher[];
  totalPages: number;
};

const getAllVouchers = async (params: { voucherType?: string }): Promise<VouchersResponse> => {
  const { voucherType } = params;
  const { data: vouchers } = await axios.get<VouchersResponse>('/management/voucher', {
    params: {
      ...(voucherType && { voucherType }),
      limit: -1,
    },
  });
  return vouchers;
};

const getVouchers = async (params: {
  voucherType?: string;
  page?: number;
}): Promise<VouchersResponse> => {
  const { voucherType, page } = params;
  const { data: vouchers } = await axios.get<VouchersResponse>('/management/voucher', {
    params: {
      ...(voucherType && { voucherType }),
      page,
    },
  });
  return vouchers;
};

export const useSchoolVouchers = (): UseQueryResult<VouchersResponse, unknown> => {
  return useQuery({
    queryKey: ['vouchers'],
    queryFn: () => getAllVouchers({}),
  });
};

export const useVouchers = (params: {
  voucherType?: string;
  page?: number;
}): UseQueryResult<VouchersResponse, unknown> => {
  const { voucherType, page } = params;
  return useQuery({
    queryKey: ['vouchers', voucherType, page],
    queryFn: () => getVouchers({ voucherType, page }),
  });
};

const getDashboardData = async (): Promise<DashboardData> => {
  const { data: dashboardData } = await axios.get<DashboardData>('/management/dashboard-2', {});
  return dashboardData;
};

export const useUserManagement = (): UseQueryResult<DashboardData | null, unknown> => {
  return useQuery({
    queryKey: ['user-management-get-vouchers'],
    queryFn: () => getDashboardData(),
  });
};

export const useUsers = ({
  voucherId,
  page,
  searchQuery,
  sortBy,
  sortOrder,
  excludeClassId,
  role,
  all,
  limit,
}: {
  voucherId?: string;
  page?: number;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: string;
  excludeClassId?: string;
  role?: SchoolUserRole[];
  all?: boolean;
  limit?: number;
}): UseQueryResult<SchoolUsers | null, unknown> => {
  return useQuery({
    queryKey: [
      'users',
      voucherId,
      page,
      searchQuery,
      sortBy,
      sortOrder,
      excludeClassId,
      role,
      all,
      limit,
    ],
    queryFn: () =>
      getUsers(voucherId, page, searchQuery, sortBy, sortOrder, excludeClassId, role, all),
  });
};

export const useKickUser = (
  refetch: () => void
): UseMutationResult<void, unknown, { userId: string; voucherId: string }, unknown> => {
  return useMutation({
    mutationFn: ({ userId, voucherId }: { userId: string; voucherId: string }) =>
      kickUserFromOrganization(userId, voucherId),
    onSuccess: () => {
      refetch();
    },
  });
};

export const useInviteUsers = (): UseMutationResult<
  AdminInviteResponse,
  unknown,
  {
    emails: string[];
    voucherId: string;
    role?: SchoolUserRole.TEACHER | SchoolUserRole.GUIDE;
  },
  unknown
> => {
  return useMutation({
    mutationFn: inviteUsers,
  });
};

export const useChangeUserRole = (
  refetch: () => void
): UseMutationResult<
  void,
  unknown,
  { userId: string; role: SchoolUserRole; confirmChange?: boolean },
  unknown
> => {
  return useMutation({
    mutationFn: changeUserRole,
    onSuccess: () => {
      refetch();
    },
  });
};
